/**
 * Lock the mobile viewport to a fixed 1:1 scale.
 *
 * The `user-scalable=no` viewport meta is enough on Android, but iOS Safari
 * deliberately ignores it (accessibility), so pinch- and double-tap-zoom still
 * fire there. This guard swallows those gestures at the document level. It does
 * NOT block single-finger panning, so scrollable dialogs (settings / "while you
 * were away") still work; page scroll is disabled separately in CSS
 * (`overflow: hidden` + `overscroll-behavior: none`).
 */
export function lockViewport(doc: Document = document): void {
  // iOS pinch fires the non-standard `gesture*` events — not in the DOM lib
  // typings, so they resolve through the plain-string addEventListener overload.
  const blockGesture: EventListener = (event) => {
    event.preventDefault();
  };
  doc.addEventListener('gesturestart', blockGesture);
  doc.addEventListener('gesturechange', blockGesture);
  doc.addEventListener('gestureend', blockGesture);

  // Pinch on engines without the gesture events: a multi-touch move.
  doc.addEventListener(
    'touchmove',
    (event) => {
      if (event.touches.length > 1) event.preventDefault();
    },
    { passive: false },
  );

  // Double-tap-to-zoom: swallow the second tap inside the ~300 ms zoom window.
  let lastTouchEnd = 0;
  doc.addEventListener(
    'touchend',
    (event) => {
      if (event.timeStamp - lastTouchEnd < 300) event.preventDefault();
      lastTouchEnd = event.timeStamp;
    },
    { passive: false },
  );
}
